import { secondMs } from "verdi-common/timeConstants";
import { SessionHandler } from "verdi-web-client";
import { queryClient } from "@/api/queryClient";
import { queryKeyForEntity } from "@/features/map-markers-v2/sources";
import type MapEntityBase from "@/services/map-management/map-entities/map-entity-base";
import { OnMapEntityCreated, OnMapEntityDeleted } from "@/services/map-management/map-entities/map-entity-base";
import { coalesce } from "@/utils/coalesce";

/**
 * The fourth cache-writing tier: a legacy entity appearing or disappearing stales the v2 list it
 * belongs to. `../README.md` covers why it carries signal rather than data.
 *
 * Scoped to lifecycle rather than to edits, deliberately. The v2 DTOs supply a marker's id and
 * coordinates and nothing else — every displayed field is read off the MapEntity — and the map is the
 * sole consumer of these three lists, so a card edit leaving a stale DTO is invisible. Coordinates, the
 * one field that matters, come through `invalidateListForMovedEntity` below, called straight from the
 * position sync. Watching `model.onChange` here would buy refetches after changes to fields nothing
 * reads.
 */

/** The list a given entity belongs to, by which source owns it — not by raw `model.category`. */
const listKeyForEntity = (mapEntity: MapEntityBase) => queryKeyForEntity(mapEntity);

/**
 * True while a session is loading, when `OnMapEntityCreated` fires per entity in bulk.
 *
 * Module-level state is implicit context rather than an argument, which holds because the two
 * session events that set it are the only writers.
 */
let isLoadingSession = false;

/** A bulk delete is many events describing one change, and one change is one refetch. */
const COALESCE_WINDOW_MS = 0.5 * secondMs;

/**
 * Lists owed an invalidation when the coalesce window elapses, keyed by the query key's hash so
 * `irrigationDevice` and `blockValve` (and devices-list `msense`) share a list and cannot invalidate
 * it twice.
 *
 * Hashed rather than held by reference: a query key is a fresh array on every read, and object
 * segments in a future parameterized key would collapse to `[object Object]` under a plain join.
 * `JSON.stringify` is what TanStack's own `queryKeyHashFn` uses.
 */
const pendingLists = new Map<string, readonly unknown[]>();

const hashOf = (queryKey: readonly unknown[]) => JSON.stringify(queryKey);

const flushPendingLists = coalesce(() => {
    const staleKeys = [...pendingLists.values()];
    pendingLists.clear();
    staleKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
}, COALESCE_WINDOW_MS);

type StaleOptions = {
    /** For a discrete event, where waiting out a burst window buys nothing. */
    immediately?: boolean;
};

const invalidateListForEntity = (mapEntity: MapEntityBase, { immediately = false }: StaleOptions = {}) => {
    if (isLoadingSession) return;

    const queryKey = listKeyForEntity(mapEntity);
    if (!queryKey) return;

    // A list nothing has read yet has nothing to stale — it refetches on first mount regardless. This is
    // load-bearing at login: `OnMapEntityCreated` fires from the MapEntityBase constructor, so a session
    // load is itself a bulk create, and without this every login would invalidate the seed it just wrote.
    if (queryClient.getQueryData(queryKey) === undefined) return;

    if (immediately) {
        // Drops a queued demand for the same list: this call already satisfies it, and leaving it would
        // fire a second refetch when the coalesce window elapses.
        pendingLists.delete(hashOf(queryKey));
        queryClient.invalidateQueries({ queryKey });
        return;
    }

    pendingLists.set(hashOf(queryKey), queryKey);
    flushPendingLists();
};

/**
 * Stales an entity's list at once, for a device that moved.
 *
 * A reposition is a discrete save, not a burst, and the marker's position comes from the DTO — so until
 * the list comes back the device sits at its old coordinates with a success snackbar on screen.
 *
 * Not poll-suppressed: a device relocated by another user arrives on a tick and genuinely should
 * refetch. The poll patches both caches itself, so the marker is right either way — the cost is one
 * request, and only for a device that actually moved.
 */
export const invalidateListForMovedEntity = (mapEntity: MapEntityBase) =>
    invalidateListForEntity(mapEntity, { immediately: true });

/**
 * Lifecycle events skip the coalesce window. An edit burst is many events describing one change, but a
 * create is one event describing one device — and the marker cannot draw until its DTO lands, so every
 * millisecond of that wait is a device the user just added sitting invisible on the map.
 */
const onEntityCreated = ({ target }: { target: MapEntityBase }) => {
    invalidateListForEntity(target, { immediately: true });
};

const onEntityDeleted = ({ target }: { target: MapEntityBase }) => {
    // Coalesced, unlike a create: the entity index drops the marker synchronously, so nothing on screen
    // is waiting for the refetch — and a bulk delete then costs one request rather than one per device.
    invalidateListForEntity(target);
};

const onSessionUnload = () => {
    isLoadingSession = true;
    // A delete queued just before an org switch would otherwise fire half a second later and
    // invalidate the list the new session just seeded.
    pendingLists.clear();
    flushPendingLists.cancel();
};

const onSessionLoad = () => {
    isLoadingSession = false;
};

/** Non-null exactly while this tier is installed, and the teardown every install hands back. */
let uninstall: (() => void) | null = null;

/**
 * `mergeUpdatesIntoList` has no removal path, so a legacy delete would linger in the v2 list cache; a
 * legacy create has no DTO in any list yet, so its marker cannot draw until one is refetched.
 *
 * Idempotent, and hands back a teardown that removes every listener it added. `setupVerdiWebClient`
 * runs again on re-login, and a second install would double every invalidation.
 */
export const installLegacyWriteInvalidation = () => {
    if (uninstall) return uninstall;

    OnMapEntityCreated.addListener(onEntityCreated);
    OnMapEntityDeleted.addListener(onEntityDeleted);
    SessionHandler.onSessionUnload.addListener(onSessionUnload);
    SessionHandler.onSessionLoad.addListener(onSessionLoad);

    uninstall = () => {
        OnMapEntityCreated.removeListener(onEntityCreated);
        OnMapEntityDeleted.removeListener(onEntityDeleted);
        SessionHandler.onSessionUnload.removeListener(onSessionUnload);
        SessionHandler.onSessionLoad.removeListener(onSessionLoad);
        pendingLists.clear();
        flushPendingLists.cancel();
        isLoadingSession = false;
        uninstall = null;
    };
    return uninstall;
};
